import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignupSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, paid, creating, success, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  
  // Account creation form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('Invalid session. Please try signing up again.');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/payments/signup-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        if (response.data.account_created) {
          // Account already exists, redirect to login
          setStatus('success');
          setTimeout(() => navigate('/app/login'), 2000);
        } else {
          // Payment successful, show account creation form
          setStatus('paid');
          setEmail(response.data.email);
        }
      } else {
        // Still processing
        setStatus('checking');
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (err) {
      console.error('Status check error:', err);
      setStatus('error');
      setError('Failed to verify payment. Please contact support.');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setCreating(true);

    try {
      const response = await axios.post(`${API}/api/payments/complete-signup`, {
        setup_token: sessionId,
        password,
        first_name: firstName,
        last_name: lastName
      });

      // Store token and user as client (matching ClientPortal storage format)
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_user', JSON.stringify(response.data.user));
      
      // Set axios header for subsequent requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      setStatus('success');
      
      // Redirect to onboarding after brief success message
      setTimeout(() => {
        navigate('/app/onboarding');
      }, 1500);
    } catch (err) {
      console.error('Account creation error:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
      setCreating(false);
    }
  };

  // Checking payment status
  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-blue-600" />
            <h2 className="text-xl font-semibold mb-2">Verifying Payment</h2>
            <p className="text-gray-500">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <Button onClick={() => navigate('/signup')}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success - account created
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2">You're All Set!</h2>
            <p className="text-gray-500">Redirecting you to get started...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment confirmed - show account creation form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Payment Successful!</CardTitle>
          <CardDescription className="text-base mt-2">
            Complete your account to get started
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {email}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
            
            <Input
              type="password"
              placeholder="Create password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
            
            <Input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            
            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              disabled={creating}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
